import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { twoFactorService } from '../../services/twoFactorService';
import { biometricService } from '../../services/biometricService';
import { authService } from '../../services/authService';
import { web3Service } from '../../services/web3Service';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';

const SignIn = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();

  // Check biometric availability on mount
  React.useEffect(() => {
    checkBiometricAvailability();
  }, []);

  const checkBiometricAvailability = async () => {
    const isSupported = biometricService?.isSupported();
    if (isSupported) {
      const isPlatformAvailable = await biometricService?.isPlatformAuthenticatorAvailable();
      setBiometricAvailable(isPlatformAvailable);
    }
  };

  const handleBiometricSignIn = async () => {
    setError('');
    setLoading(true);

    try {
      // Get biometric methods for current session
      const methods = await biometricService?.getBiometricMethods();
      
      if (methods?.length === 0) {
        setError('No biometric authentication methods registered. Please sign in with email and password.');
        setLoading(false);
        return;
      }

      // Use the first available biometric method
      const method = methods?.[0];
      await biometricService?.verifyBiometric(method?.id);
      
      // On successful biometric verification, proceed to dashboard
      navigate('/user-wallet-dashboard');
    } catch (err) {
      setError(err?.message || 'Biometric authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { data, error } = await authService?.signIn(email, password);
      
      if (error) {
        // Check if error is related to email verification
        if (error?.message?.toLowerCase()?.includes('email') || error?.message?.toLowerCase()?.includes('confirm')) {
          setError('Please verify your email before signing in. Check your inbox for the verification link.');
        } else {
          setError(error?.message);
        }
        setLoading(false);
        return;
      }

      // Check if email is verified
      const { data: profile } = await authService?.getUserProfile();
      
      if (profile && !profile?.emailVerified) {
        // Sign out the user
        await authService?.signOut();
        setError('Please verify your email before signing in. Check your inbox for the verification code.');
        setLoading(false);
        return;
      }

      // Check if 2FA is enabled
      if (data?.user) {
        const { data: twoFactorData } = await twoFactorService?.checkTwoFactorStatus();
        
        if (twoFactorData?.enabled) {
          // Redirect to 2FA verification
          navigate('/two-factor-authentication-verification');
          return;
        }
      }

      // Successful login without 2FA
      navigate('/user-wallet-dashboard');
    } catch (err) {
      setError(err?.message || 'An error occurred during sign in');
    } finally {
      setLoading(false);
    }
  };

  const fillDemoCredentials = (userType) => {
    if (userType === 'free') {
      setEmail('user@example.com');
      setPassword('password123');
    } else if (userType === 'pro') {
      setEmail('pro@example.com');
      setPassword('password123');
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-card rounded-lg shadow-lg p-8 border border-border">
          <div className="flex items-center justify-center mb-6">
            <div className="bg-primary rounded-full p-3">
              <img src="/assets/images/22458-1768718790784.png" alt="BOLTPay Logo" className="w-8 h-8 object-contain" />
            </div>
          </div>
          
          <h1 className="text-2xl font-bold text-center text-foreground mb-2">
            Welcome to BOLTPay
          </h1>
          <p className="text-center text-muted-foreground mb-6">
            Sign in to access your wallet and payment features
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              type="email"
              label="Email"
              value={email}
              onChange={(e) => setEmail(e?.target?.value)}
              placeholder="Enter your email"
              required
              error={error && !email ? 'Email is required' : ''}
            />

            <Input
              type="password"
              label="Password"
              value={password}
              onChange={(e) => setPassword(e?.target?.value)}
              placeholder="Enter your password"
              required
              error={error && !password ? 'Password is required' : ''}
            />

            <div className="text-right">
              <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                Forgot password?
              </Link>
            </div>

            {error && (
              <div className="bg-error/10 border border-error rounded-md p-3 flex items-start gap-2">
                <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
                <p className="text-sm text-error">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              fullWidth
              loading={loading}
              iconName="LogIn"
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-card text-muted-foreground">Or continue with</span>
              </div>
            </div>

            <div className="mt-4 space-y-3">
              <Button
                type="button"
                variant="outline"
                fullWidth
                onClick={async () => {
                  setError('');
                  setLoading(true);
                  try {
                    await authService?.signInWithGoogle();
                  } catch (err) {
                    setError(err?.message || 'Google sign-in failed');
                    setLoading(false);
                  }
                }}
                disabled={loading}
                className="flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>

              <Button
                type="button"
                variant="outline"
                fullWidth
                onClick={async () => {
                  setError('');
                  setLoading(true);
                  try {
                    // Connect Ethereum wallet
                    const { address, walletType, error: connectError } = await web3Service?.connectMetaMask();
                    
                    if (connectError) {
                      throw new Error(connectError?.message);
                    }

                    // Sign authentication message
                    const message = `Sign this message to authenticate with BOLTPay: ${address}`;
                    const { signature, error: signError } = await web3Service?.signMessageEthereum(message);
                    
                    if (signError) {
                      throw new Error(signError?.message);
                    }

                    // Authenticate with Supabase
                    const { data, error: authError } = await authService?.signInWithEthereumWallet(address, signature);
                    
                    if (authError) {
                      throw new Error(authError?.message);
                    }

                    // Redirect to dashboard
                    navigate('/user-wallet-dashboard');
                  } catch (err) {
                    setError(err?.message || 'Ethereum wallet connection failed');
                    setLoading(false);
                  }
                }}
                disabled={loading}
                className="flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" viewBox="0 0 256 417" fill="none">
                  <path d="M127.961 0L125.635 7.88867V285.168L127.961 287.489L255.922 212.32L127.961 312.187L126.635 313.777V406.603L127.961 410.999L256 237.023L127.961 312.187Z" fill="#343434"/>
                  <path d="M127.962 0L0 212.32L127.962 287.489V153.763V0Z" fill="#8C8C8C"/>
                  <path d="M127.961 312.187L126.635 313.777V406.603L127.961 410.999L256 237.023L127.961 312.187Z" fill="#3C3C3B"/>
                  <path d="M127.962 410.999V312.187L0 237.023L127.962 410.999Z" fill="#8C8C8C"/>
                  <path d="M127.961 287.489L255.922 212.32L127.961 153.763V287.489Z" fill="#141414"/>
                  <path d="0 212.32L127.962 287.489V153.763L0 212.32Z" fill="#393939"/>
                </svg>
                Connect Ethereum Wallet
              </Button>

              <Button
                type="button"
                variant="outline"
                fullWidth
                onClick={async () => {
                  setError('');
                  setLoading(true);
                  try {
                    // Connect Solana wallet
                    const { address, walletType, error: connectError } = await web3Service?.connectSolanaWallet();
                    
                    if (connectError) {
                      throw new Error(connectError?.message);
                    }

                    // Sign authentication message
                    const message = `Sign this message to authenticate with BOLTPay: ${address}`;
                    const { signature, publicKey, error: signError } = await web3Service?.signMessageSolana(message);
                    
                    if (signError) {
                      throw new Error(signError?.message);
                    }

                    // Authenticate with Supabase
                    const { data, error: authError } = await authService?.signInWithSolanaWallet(address, signature, publicKey);
                    
                    if (authError) {
                      throw new Error(authError?.message);
                    }

                    // Redirect to dashboard
                    navigate('/user-wallet-dashboard');
                  } catch (err) {
                    setError(err?.message || 'Solana wallet connection failed');
                    setLoading(false);
                  }
                }}
                disabled={loading}
                className="flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" viewBox="0 0 397.7 311.7" fill="none">
                  <defs>
                    <linearGradient id="solanaGradient" x1="360.88" y1="351.46" x2="141.21" y2="-69.29" gradientUnits="userSpaceOnUse">
                      <stop offset="0" stopColor="#00FFA3"/>
                      <stop offset="1" stopColor="#DC1FFF"/>
                    </linearGradient>
                    <linearGradient id="solanaGradient2" x1="264.83" y1="401.6" x2="45.16" y2="-19.15" gradientUnits="userSpaceOnUse">
                      <stop offset="0" stopColor="#00FFA3"/>
                      <stop offset="1" stopColor="#DC1FFF"/>
                    </linearGradient>
                    <linearGradient id="solanaGradient3" x1="312.55" y1="376.69" x2="92.88" y2="-44.06" gradientUnits="userSpaceOnUse">
                      <stop offset="0" stopColor="#00FFA3"/>
                      <stop offset="1" stopColor="#DC1FFF"/>
                    </linearGradient>
                  </defs>
                  <path d="M64.6 237.9c2.4-2.4 5.7-3.8 9.2-3.8h317.4c5.8 0 8.7 7 4.6 11.1l-62.7 62.7c-2.4 2.4-5.7 3.8-9.2 3.8H6.5c-5.8 0-8.7-7-4.6-11.1l62.7-62.7z" fill="url(#solanaGradient)"/>
                  <path d="M64.6 3.8C67.1 1.4 70.4 0 73.8 0h317.4c5.8 0 8.7 7 4.6 11.1l-62.7 62.7c-2.4 2.4-5.7 3.8-9.2 3.8H6.5c-5.8 0-8.7-7-4.6-11.1L64.6 3.8z" fill="url(#solanaGradient2)"/>
                  <path d="M333.1 120.1c-2.4-2.4-5.7-3.8-9.2-3.8H6.5c-5.8 0-8.7 7-4.6 11.1l62.7 62.7c2.4 2.4 5.7 3.8 9.2 3.8h317.4c5.8 0 8.7-7 4.6-11.1l-62.7-62.7z" fill="url(#solanaGradient3)"/>
                </svg>
                Connect Solana Wallet
              </Button>
            </div>
          </div>

          {biometricAvailable && (
            <div className="mt-4">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-card text-muted-foreground">Or use biometrics</span>
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                fullWidth
                onClick={handleBiometricSignIn}
                disabled={loading}
                iconName="Fingerprint"
                className="mt-4"
              >
                Sign in with Biometrics
              </Button>
            </div>
          )}

          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              Don't have an account?{' '}
              <Link to="/signup" className="text-primary hover:underline font-semibold">
                Sign Up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;